import { Component, ViewEncapsulation, OnInit, ViewChildren,
	QueryList, ElementRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { WizardStepperService } from '../../../services/wizard-stepper.service';
import { WizardStep } from '../../../models/app-wizard-state';
import { AppWizardService } from '../../../services/app-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { FormOffer, FormFamilyByVolumes, FormProducts } from '../../../models/form-offer';
import { OFFER_TYPE } from '../../../models/offer-utils';
import { isoMonthParse, isoMonthToDate,
	isoMonthToMoment } from '../../../models/iso-week-or-month-utils';
import { PeriodIsoMonth } from '../../../models/month-week-value';
import { ProductFamily } from '../../../models/offer';
import { FamilyOfProducts } from '../../../models/offer-wizard';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { TranslationService } from '../../../../shared/services/translation.service';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { OfferPcpSplitComponent } from '../../utils/offer-pcp-split/offer-pcp-split.component';
import { ProductListQuantityComponent } from '../../utils/product-list-quantity/product-list-quantity.component';
import { IconService } from '../../../../shared/services/icon.service';

@Component({
	selector: 'copo-offer-wizard-step-review',
	templateUrl: './step-review.component.html',
	styleUrl: './step-review.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,
		MatTooltipModule,
		MatFormFieldModule,
		FormsModule,
		ReactiveFormsModule,
		NgFor,
		TitleCasePipe,
		OfferPcpSplitComponent,
		ProductListQuantityComponent,
		MatIconModule
	]
})
export class StepReviewComponent extends WizardPageContent implements OnInit {
	@ViewChildren('rows') rows!: QueryList<ElementRef<HTMLElement>>;
	@ViewChildren('table') readonly tables!: QueryList<ProductListQuantityComponent>;
	@ViewChildren('offerPcpSplit') readonly offerFamilyByVolume!: QueryList<OfferPcpSplitComponent>;

	readonly UNDERSCORE = UNDERSCORE;
	readonly form: FormOffer;

	rfqRef = '';
	soldTo = '';
	shipTo = '';
	endUser = '';
	period = '';
	productFamilies: ProductFamily[] = [];
	productsGroupedByFamily = new Map<string, FamilyOfProducts>();
	selectedPeriodDateDisplay = '';
	selectedPeriodDisplay = '';
	offerType: string | null | undefined;

	isLoading = true;
	isMerged = false;

	private selectedPeriod: PeriodIsoMonth | undefined;
	private readonly VIEW_STEP_NUM = 3;

	constructor(
		private readonly appWizardService: AppWizardService,
		private readonly stepperService: WizardStepperService,
		readonly iconService: IconService,
		private readonly translationService: TranslationService
	) {
		iconService.registerAllIcons();
		super(WizardStep.REVIEW, appWizardService.getState());
		const state = this.appWizardService.getState();
		this.offerType = state.offer.getOfferType() || sessionStorage.getItem(OFFER_TYPE);
		this.form = this.appWizardService.getStateOfferForm();
	}

	get getProductFamiliesHavingItemInCart() {
		return this.productFamilies.filter(
			productFamily => productFamily.code && this.getProductsCountByCode(productFamily.code) > 0
		);
	}

	get isError() {
		return this.appWizardService.getState().errorInPage;
	}

	ngOnInit() {
		this.stepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		if (this.form.getFormPeriod().getFormPeriodStartMonth().value) {
			this.selectedPeriod = new PeriodIsoMonth(
				isoMonthParse(<string> this.form.getFormPeriod().getFormPeriodStartMonth().value),
				isoMonthParse(<string> this.form.getFormPeriod().getFormPeriodEndMonth().value)
			);
		}
		this.rfqRef = <string> this.form.getFormRfqRef().value;
		const selSoldToId = <string> this.form.getFormSoldTo().value;
		if (this.appWizardService.soldToList) {
			const selectedSoldTo = this.appWizardService.soldToList.find(v => v.businessId === selSoldToId);
			if (selectedSoldTo) {
				this.soldTo = `${ selectedSoldTo.name } / ${ selectedSoldTo.city }`;
			}
		}

		const selShipToId = <string> this.form.getFormShipTo().value;
		if (this.appWizardService.shipToList) {
			const selectedShipTo = this.appWizardService.shipToList.find(v => v.businessId === selShipToId);
			if (selectedShipTo) {
				this.shipTo = `${ selectedShipTo.name } / ${ selectedShipTo.city }`;
			}
		}

		const selEndUserId = <string> this.form.getFormEndUser().value;
		if (this.appWizardService.endUserList) {
			const selectedEndUser = this.appWizardService.endUserList.find(v => v.businessId === selEndUserId);
			if (selectedEndUser) {
				this.endUser = `${ selectedEndUser.name } / ${ selectedEndUser.city }`;
			}
		}
		this.createFormProductsByFamily(selSoldToId);
		if (this.offerType !== 'spot') {
			this.addFormProductsByFamily();
		}
		this.processPeriodInfo();
		this.generateQuantityForProductFamily();
		this.isLoading = false;
	}

	checkError(): boolean {
		return false;
	}

	checkErrorForDraft(): boolean {
		return false;
	}

	scrollToGivenFamily(index: number) {
		const rowItems = this.rows.toArray();
		if (rowItems[index]) {
			rowItems[index].nativeElement.scrollIntoView();
		}
	}

	getFormFamilyByVolumes(code: string) {
		return this.productsGroupedByFamily.get(code)?.formFamilyByVolumes;
	}

	getQuantityByFamilyCode(code: string) {
		return this.productsGroupedByFamily.get(code)?.totalQuantity || 0;
	}

	getFromDate() {
		if (!this.selectedPeriod) {
			throw new Error('undefined selected period');
		}
		return isoMonthToDate(this.selectedPeriod.start);
	}

	getToDate() {
		if (!this.selectedPeriod) {
			throw new Error('undefined selected period');
		}
		return isoMonthToMoment(this.selectedPeriod.end).endOf('month').toDate();
	}

	getProductsCountByCode(code: string) {
		return this.appWizardService.productsGroupedByFamily.get(code) ?? 0;
	}

	getUniqueRefListByCode(code: string) {
		const products = this.productsGroupedByFamily.get(code)?.formProducts.getProducts();
		if (products && products.length > 0) {
			return products.map(product => product.uniqueRef)
				.filter(uniqueRef => uniqueRef !== undefined);
		}
		return [];
	}

	getformProductsByCode(code: string) {
		return this.productsGroupedByFamily.get(code)?.formProducts || new FormProducts();
	}

	private processPeriodInfo() {
		let startMonthValue = <string> this.form.getFormPeriod().getFormPeriodStartMonth().value;
		let endMonthValue = <string> this.form.getFormPeriod().getFormPeriodEndMonth().value;

		const currentDate = new Date();
		if (startMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const startDate = new Date(startYear, startMonth - 1, 1);
			if (startDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodStartMonth().setValue('');
				startMonthValue = '';
			}
		}

		if (endMonthValue) {
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const endDate = new Date(endYear, endMonth - 1, 1);
			if (endDate < currentDate) {
				this.form.getFormPeriod().getFormPeriodEndMonth().setValue('');
				endMonthValue = '';
			}
		}

		if (!this.form.getFormPeriod().getFormPeriodStartMonth().value
			|| !this.form.getFormPeriod().getFormPeriodEndMonth().value) {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}

		if (startMonthValue && endMonthValue) {
			const [startYear, startMonth] = startMonthValue.split('-M').map(Number);
			const [endYear, endMonth] = endMonthValue.split('-M').map(Number);
			const monthDifference = (endYear - startYear) * 12 + (endMonth - startMonth);

			const selectedPeriodType =
					startYear === endYear && startMonth === endMonth ? 'Monthly'
						: monthDifference === 2 && [1, 4, 7, 10].includes(startMonth) ? 'Quarterly'
							: monthDifference === 5 && [1, 7].includes(startMonth) ? 'Semesterly'
								: startMonth === 1 && endMonth === 12 && startYear === endYear ? 'Annually'
									: 'Custom Period';
			switch (selectedPeriodType) {
			case 'Monthly':
				this.selectedPeriodDisplay = `${ moment().month(startMonth - 1).format('MMMM') } (${ startYear })`;
				break;

			case 'Quarterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Quarter') }
				 ${ Math.floor((startMonth - 1) / 3) + 1 } (${ startYear })`;
				break;

			case 'Semesterly':
				this.selectedPeriodDisplay = `${ this.translationService.getTR('eoffer-Semester') }
				 ${ Math.floor((startMonth - 1) / 6) + 1 } (${ startYear })`;
				break;

			case 'Annually':
				this.selectedPeriodDisplay = `${ startYear }`;
				break;

			default:
				this.selectedPeriodDisplay = '';
				break;
			}
		}
		this.updateSelectedPeriodDisplay(startMonthValue, endMonthValue);
	}

	private updateSelectedPeriodDisplay(fromMonth: string, toMonth?: string) {
		if (fromMonth && toMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } - ${ toMonth }`;
		} else if (fromMonth) {
			this.selectedPeriodDateDisplay = `${ fromMonth } -`;
		} else if (toMonth) {
			this.selectedPeriodDateDisplay = `- ${ toMonth }`;
		} else {
			this.selectedPeriodDateDisplay = this.translationService.getTR('global.none');
		}
	}

	private createFormProductsByFamily(soldTo: string) {
		this.productFamilies = this.appWizardService.getState().ui.getProductFamilies(soldTo) || [];
		const formProucts = this.form.getFormProducts();
		for (const formProduct of formProucts.getFormProducts()) {
			const product = formProduct.getProduct();
			const productFamily = this.productFamilies.find(
				pf =>
					product.category === pf.category
					&& product.subCategory === pf.subCategory
					&& product.form === pf.form
			);
			if (productFamily) {
				let familyOfProduct = this.productsGroupedByFamily.get(productFamily.code);
				if (familyOfProduct) {
					familyOfProduct.formProducts.push(formProduct);
					continue;
				}
				const localFormProducts = new FormProducts(formProucts);
				localFormProducts.push(formProduct);
				familyOfProduct = {
					familyCode: productFamily.code,
					formProducts: localFormProducts
				};
				this.productsGroupedByFamily.set(productFamily.code, familyOfProduct);
			}
		}
	}

	private addFormProductsByFamily() {
		const globalFormFamilyByVolumes = this.appWizardService.getStateOfferForm().getFormFamilyByVolumes();
		for (const [, familyofProduct] of this.productsGroupedByFamily) {
			const localFamilyByVolumes = new FormFamilyByVolumes(globalFormFamilyByVolumes);
			for (const formFamilyByVolume of globalFormFamilyByVolumes.getFormFamilyByVolumes()) {
				if (formFamilyByVolume.getFamilyByVolume().familyCode === familyofProduct.familyCode) {
					localFamilyByVolumes.push(formFamilyByVolume);
				}
			}
			familyofProduct.formFamilyByVolumes = localFamilyByVolumes;
		}
	}

	private generateQuantityForProductFamily() {
		for (const [,familyOfProduct] of this.productsGroupedByFamily) {
			familyOfProduct.totalQuantity = 0;
			if (this.offerType === 'spot') {
				for (const product of familyOfProduct.formProducts.getFormProducts()) {
					const quantity = <number | undefined> product.getFormQuantity().value;
					if (quantity) {
						familyOfProduct.totalQuantity += Number(quantity);
					}
				}
			} else {
				const globalFormFamilyByVolumes = this.appWizardService.getStateOfferForm().getFormFamilyByVolumes();
				for (const formFamilyByVolume of globalFormFamilyByVolumes.getFormFamilyByVolumes()) {
					if (formFamilyByVolume.getFamilyByVolume().familyCode === familyOfProduct.familyCode) {
						const quantity = <number | undefined> formFamilyByVolume.getFormQuantity().value;
						if (quantity) {
							familyOfProduct.totalQuantity += Number(quantity);
						}
					}
				}
			}
		}
	}
}
